"use no memo";

import { isInteractiveDialog } from "@choose/ui";
import { Badge } from "@choose/ui/badge";
import { Checkbox } from "@choose/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@choose/ui/tooltip";
import { Link } from "@tanstack/react-router";
import type {
  ColumnDef,
  Row,
  RowSelectionState,
  TableState,
  ColumnVisibilityState,
} from "@tanstack/react-table";
import { useTable } from "@tanstack/react-table";
import { CircleAlertIcon, PackagePlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";

import type { DataListFeatures } from "~/components/ui/data-list/data-list";
import {
  DataList,
  dataListFeatures,
} from "~/components/ui/data-list/data-list";
import { DataListVirtual } from "~/components/ui/data-list/data-list-virtual";
import type {
  SearchOrdersQuery,
  SupportOrderFlowErrorCode,
} from "~/graphql/graphql.gen";
import { useSelectRows } from "~/hooks/use-select-rows";
import { formatDate } from "~/libs/date";
import { toLocale } from "~/libs/locale";
import { formatCurrency } from "~/libs/number";
import { getGroupActionErrors } from "~/stores/group-actions-store";
import {
  getOrderParcelStatus,
  getOrderParcelStatuses,
  sortTrackingStatus,
} from "~/stores/orders-store";

export const NEXT_ITEM_SHORTCUT = "ArrowDown";
export const PREVIOUS_ITEM_SHORTCUT = "ArrowUp";
export const SELECT_ITEM_SHORTCUT = "x";
export const BULK_SELECT_ITEM_SHORTCUT = "shift+x";

export type Order = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<SearchOrdersQuery["orders_search"]>["edges"]
    >[number]
  >["node"]
> & { error?: SupportOrderFlowErrorCode | null };

const ROW_SELECTOR = "a[data-slot^=data-list-row]";

export const TABLE_ACCESSOR_AMOUNT = "amount";
export const TABLE_ACCESSOR_BRAND_NAME = "brandName";
export const TABLE_ACCESSOR_CLAIM_REASON = "claimReason";
export const TABLE_ACCESSOR_CREATED_AT = "createdAt";
export const TABLE_ACCESSOR_ERROR = "error";
export const TABLE_ACCESSOR_ID = "id";
export const TABLE_ACCESSOR_IS_RESHIP = "isReship";
export const TABLE_ACCESSOR_RECIPIENT_NAME = "recipientName";
export const TABLE_ACCESSOR_SHIPPING_STATUS = "shippingStatus";

export const DEFAULT_COLUMN_VISIBILITY: ColumnVisibilityState = {
  [TABLE_ACCESSOR_CLAIM_REASON]: false,
  [TABLE_ACCESSOR_ERROR]: false,
};

export const SELECTABLE_TABLE_DEFAULT_STATE: Partial<
  TableState<DataListFeatures>
> = {
  columnOrder: [
    "select",
    TABLE_ACCESSOR_SHIPPING_STATUS,
    TABLE_ACCESSOR_ID,
    TABLE_ACCESSOR_BRAND_NAME,
    TABLE_ACCESSOR_RECIPIENT_NAME,
    TABLE_ACCESSOR_AMOUNT,
    "alt",
    TABLE_ACCESSOR_IS_RESHIP,
    TABLE_ACCESSOR_CREATED_AT,
  ],
  columnPinning: {
    start: ["select"],
    end: [],
  },
};

export function OrderList({
  data,
  state,
  onFetchMore,
  isFetchingMore,
  hasMore,
}: {
  data: Order[];
  state: Partial<TableState<DataListFeatures>>;
  onFetchMore?: () => void;
  isFetchingMore?: boolean;
  hasMore?: boolean;
}) {
  const { i18n, t } = useTranslation();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const selectRows = useSelectRows<Row<DataListFeatures, Order>>();

  const columns = useMemo<ColumnDef<DataListFeatures, Order>[]>(
    () => [
      {
        id: "select",
        cell: ({ row, table }) => (
          <div
            className="flex items-center justify-center"
            data-list-grid-column="select"
          >
            <Checkbox
              tabIndex={-1}
              checked={row.getIsSelected()}
              indeterminate={row.getIsSomeSelected() && !row.getIsSelected()}
              disabled={!row.getCanSelect()}
              onClick={(e) => {
                e.stopPropagation();

                // Focus the row element for keyboard navigation
                const rowElement = (e.target as HTMLElement).closest(
                  ROW_SELECTOR,
                );
                if (rowElement) (rowElement as HTMLAnchorElement).focus();

                selectRows(table.getRowModel(), row, e.shiftKey);
              }}
              className="relative opacity-0 after:absolute after:inset-[-15px_-8px_-15px_-10px] hover:opacity-100 data-checked:opacity-100"
            />
          </div>
        ),
      },
      {
        id: TABLE_ACCESSOR_SHIPPING_STATUS,
        cell: ({ row }) => {
          const order = row.original;
          const firstSortedParcel = order.parcels.toSorted((a, b) =>
            sortTrackingStatus(a.tracking.status, b.tracking.status),
          )[0];
          const parcelStatus = getOrderParcelStatuses(t).find(
            (status) =>
              status.value ===
              getOrderParcelStatus({
                trackingStatus: firstSortedParcel?.tracking.status,
                trackingSubStatus: firstSortedParcel?.tracking.subStatus,
              }),
          );

          return (
            <div className="shrink-0">
              {parcelStatus ? (
                <parcelStatus.icon className="size-4 shrink-0" />
              ) : null}
            </div>
          );
        },
      },
      {
        id: TABLE_ACCESSOR_RECIPIENT_NAME,
        cell: ({ row }) => {
          const order = row.original;
          const recipient = order.recipient;

          return (
            <div className="text-muted-foreground flex w-40 flex-[initial] shrink-0 items-center">
              <span className="truncate">{`${recipient.firstName} ${recipient.lastName}`}</span>
            </div>
          );
        },
      },
      {
        id: TABLE_ACCESSOR_ID,
        accessorKey: "id",
        cell: ({ cell }) => {
          const value = cell.getValue<string>();
          return (
            <span className="text-muted-foreground max-w-58 min-w-28 shrink grow truncate">
              {value}
            </span>
          );
        },
      },
      {
        id: TABLE_ACCESSOR_AMOUNT,
        accessorKey: "totalPriceSold.valueWithVat",
        cell: ({ cell, row }) => {
          const value = cell.getValue<string>();

          return (
            <span className="max-w-58 min-w-28 shrink grow truncate">
              {formatCurrency(Number(value) / 100, toLocale(i18n.language), {
                currency: row.original.totalPriceSold.currency,
                currencyDisplay: "narrowSymbol",
              })}
            </span>
          );
        },
      },
      {
        id: TABLE_ACCESSOR_BRAND_NAME,
        accessorKey: "brand.name",
        cell: ({ cell }) => {
          const value = cell.getValue<string>();
          return (
            <div className="flex w-40 flex-[initial] shrink-0 items-center">
              <span className="truncate">{value}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "alt",
        cell: () => {
          return <div className="flex flex-[initial] grow"></div>;
        },
      },
      {
        id: TABLE_ACCESSOR_IS_RESHIP,
        accessorKey: "initialOrder",
        cell: ({ cell }) => {
          const value = cell.getValue<{ id: string } | null>();
          return value ? (
            <div className="flex flex-[initial] shrink-0 items-center">
              <Tooltip>
                <TooltipTrigger>
                  <PackagePlusIcon className="size-4 text-orange-600" />
                </TooltipTrigger>
                <TooltipContent>{t("order.reship_order")}</TooltipContent>
              </Tooltip>
            </div>
          ) : null;
        },
      },
      {
        id: TABLE_ACCESSOR_CREATED_AT,
        accessorKey: "createdAt",
        cell: ({ cell }) => {
          const value = cell.getValue<string>();

          return (
            <div className="flex flex-[initial] shrink-0 items-center">
              <Tooltip>
                <TooltipTrigger>
                  {formatDate(new Date(value), toLocale(i18n.language), {
                    day: "numeric",
                    month: "numeric",
                    year: "numeric",
                  })}
                </TooltipTrigger>
                <TooltipContent>
                  <p>{new Date(cell.getValue<string>()).toUTCString()}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          );
        },
      },
      {
        id: TABLE_ACCESSOR_CLAIM_REASON,
        accessorKey: "tags.claimReason",
        cell: ({ cell }) => {
          return (
            <div className="flex flex-[initial] shrink-0 items-center">
              <p>{cell.getValue<string[]>().join(", ")}</p>
            </div>
          );
        },
      },
      {
        id: TABLE_ACCESSOR_ERROR,
        accessorKey: "error",
        cell: ({ cell }) => {
          const error = cell.getValue<SupportOrderFlowErrorCode | null>();
          const groupActionError = getGroupActionErrors(t).find(
            (err) => err.value === error,
          );
          if (!error) return null;

          return (
            <div className="flex flex-[initial] shrink-0 items-center">
              <Badge variant="outline" className="flex items-center gap-1">
                <CircleAlertIcon className="size-4 text-red-600" />
                {groupActionError?.label ?? ""}
              </Badge>
            </div>
          );
        },
      },
    ],
    [i18n.language, selectRows, t],
  );

  const table = useTable({
    features: dataListFeatures,
    data,
    columns,
    state: {
      ...state,
      rowSelection,
    },
    enableRowSelection: true,
    getRowId: (row) => row.id,
    onRowSelectionChange: setRowSelection,
    debugTable: true,
  });

  useHotkeys(
    SELECT_ITEM_SHORTCUT,
    (e) => {
      e.preventDefault();

      if (!(e.target instanceof Element)) return;

      const rowId = (e.target as HTMLAnchorElement).getAttribute("data-row-id");

      if (!rowId) return;

      selectRows(table.getRowModel(), table.getRow(rowId), false);
    },
    {
      ignoreEventWhen: (e) => isInteractiveDialog(e),
      useKey: true,
    },
  );

  useHotkeys(
    BULK_SELECT_ITEM_SHORTCUT,
    (e) => {
      e.preventDefault();

      if (!(e.target instanceof Element)) return;

      const rowId = (e.target as HTMLAnchorElement).getAttribute("data-row-id");

      if (!rowId) return;

      selectRows(table.getRowModel(), table.getRow(rowId), true);
    },
    {
      ignoreEventWhen: (e) => isInteractiveDialog(e),
      useKey: true,
    },
  );

  useHotkeys(
    `${PREVIOUS_ITEM_SHORTCUT}, ${NEXT_ITEM_SHORTCUT}`,
    (e) => {
      e.preventDefault();

      if (e.key === PREVIOUS_ITEM_SHORTCUT) {
        if (!(e.target instanceof Element)) return;

        const currentItem = e.target.closest(ROW_SELECTOR);
        if (!currentItem) {
          const firstItem = document.querySelector(ROW_SELECTOR);

          if (firstItem) (firstItem as HTMLAnchorElement).focus();
        } else {
          const items = [...document.querySelectorAll(ROW_SELECTOR)];
          const previousItem = items[items.indexOf(currentItem) - 1];
          const itemToScrollTo = items[items.indexOf(currentItem) - 2];
          if (itemToScrollTo)
            itemToScrollTo.scrollIntoView({ block: "nearest" });

          if (previousItem && previousItem instanceof HTMLAnchorElement)
            previousItem.focus();
        }
      } else if (e.key === NEXT_ITEM_SHORTCUT) {
        if (!(e.target instanceof Element)) return;

        const currentItem = e.target.closest(ROW_SELECTOR);
        if (!currentItem) {
          const firstItem = document.querySelector(ROW_SELECTOR);

          if (firstItem) (firstItem as HTMLAnchorElement).focus();
        } else {
          const items = [...document.querySelectorAll(ROW_SELECTOR)];
          const nextItem = items[items.indexOf(currentItem) + 1];
          const itemToScrollTo = items[items.indexOf(currentItem) + 2];
          if (itemToScrollTo)
            itemToScrollTo.scrollIntoView({ block: "nearest" });

          if (nextItem && nextItem instanceof HTMLAnchorElement)
            nextItem.focus();
        }
      }
    },
    {
      ignoreEventWhen: (e) => isInteractiveDialog(e),
    },
  );

  return (
    <DataList
      key="order-list"
      isLoading={false}
      pageSize={25}
      renderRow={({ id, children }) => (
        <Link
          to="/order/$orderId"
          params={{
            orderId: id,
          }}
        >
          {children}
        </Link>
      )}
    >
      <div
        data-slot="scroll-area-viewport"
        className="h-full overflow-x-hidden overflow-y-auto"
      >
        <DataListVirtual
          estimateSize={44}
          onFetchMore={onFetchMore}
          overscan={8}
          isFetchingMore={isFetchingMore}
          hasMore={hasMore}
          table={table}
        />
      </div>
    </DataList>
  );
}
